import type {RootState} from '@/store';
import type {TimelineItemData} from '../timeline.types';
import {SHOW_ITEM_TIMELINE_TRACK, SNAP_THRESHOLD_PX, TimelineItemType, ITEM_TRACK_HEIGHT, TRACK_GAP, TIMELINE_HORIZONTAL_PADDING} from '../timeline.types';
import type {SelectedTimelineItem} from '../timeline.types';
import type {DragModifyingData, VerticalDragParams, VerticalDragResult} from '../components/timeline-item/timeline-item.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {formatSecondsToTimeString} from '@Utils/date.util';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item.library';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';

export const timeToPixels = (time: number, pixelsPerSecond: number): number => {
  return time * pixelsPerSecond;
};

export const pixelsToTime = (pixels: number, pixelsPerSecond: number): number => {
  return pixels / pixelsPerSecond;
};

/**
 * Horizontal inset (px) from the scroll container's content origin to the t=0 mark.
 * The desktop timeline pads its track by a fixed gutter, so t=0 sits a constant distance in.
 * The mobile timeline instead pads by half the viewport so t=0 can rest under the fixed,
 * centered playhead — making its inset half the live container width, not the fixed gutter.
 */
export const getTimelineContentLeftPadding = (container: HTMLElement): number => {
  return isEditorMobileVariant() ? container.clientWidth / 2 : TIMELINE_HORIZONTAL_PADDING;
};

/**
 * Page-relative time (seconds, floored at 0) under a viewport `clientX`, accounting for the
 * container's horizontal scroll and the layout's content padding. Shared so the drag-end seek
 * and the drag cursor-time agree on both the desktop gutter and the mobile centered playhead.
 */
export const clientXToRelativeTime = (container: HTMLElement, clientX: number, pixelsPerSecond: number): number => {
  const containerRect = container.getBoundingClientRect();
  const relativeX = clientX - containerRect.left + container.scrollLeft - getTimelineContentLeftPadding(container);
  return Math.max(0, pixelsToTime(relativeX, pixelsPerSecond));
};

export const getItemWidth = (startTime: number, endTime: number, pixelsPerSecond: number): number => {
  return timeToPixels(endTime - startTime, pixelsPerSecond);
};

export const getItemLeft = (startTime: number, pixelsPerSecond: number): number => {
  return timeToPixels(startTime, pixelsPerSecond);
};

/**
 * For a horizontal drag-to-reorder of `orderedBackgrounds`, returns the gap index
 * where the dragged item would land — based on whether the cursor has crossed
 * each non-dragged item's midpoint.
 */
export const computeBackgroundInsertionIndex = (orderedBackgrounds: ItemObject[], draggedId: string, cursorTime: number): number => {
  const others = orderedBackgrounds.filter((bg) => bg.uid !== draggedId);
  for (let i = 0; i < others.length; i++) {
    const midTime = (others[i].onPageStartTime + others[i].onPageEndTime) / 2;
    if (cursorTime < midTime) {
      return i;
    }
  }
  return others.length;
};

/**
 * Returns the page-time at which the insertion indicator should render, given the
 * insertion index returned by `computeBackgroundInsertionIndex`.
 */
export const getBackgroundInsertionTime = (orderedBackgrounds: ItemObject[], draggedId: string, insertionIndex: number): number => {
  const others = orderedBackgrounds.filter((bg) => bg.uid !== draggedId);
  if (others.length === 0) {
    return 0;
  }
  if (insertionIndex <= 0) {
    return others[0].onPageStartTime;
  }
  if (insertionIndex >= others.length) {
    return others[others.length - 1].onPageEndTime;
  }
  return others[insertionIndex].onPageStartTime;
};

const detectOverlap = (item1: TimelineItemData, item2: TimelineItemData): boolean => {
  if (item1.id === item2.id) {
    return false;
  }
  return item1.startTime < item2.endTime && item1.endTime > item2.startTime;
};

export const getMaxSubRow = (items: TimelineItemData[]): number => {
  if (items.length === 0) {
    return 0;
  }
  return Math.max(...items.map((item) => item.subRow));
};

export const calculateDraggedTimes = (originalStartTime: number, originalEndTime: number, deltaPixels: number, pixelsPerSecond: number): {startTime: number; endTime: number} => {
  const deltaTime = pixelsToTime(deltaPixels, pixelsPerSecond);
  const duration = originalEndTime - originalStartTime;

  let newStartTime = originalStartTime + deltaTime;
  let newEndTime = originalEndTime + deltaTime;

  if (newStartTime < 0) {
    newStartTime = 0;
    newEndTime = duration;
  }

  return {
    startTime: newStartTime,
    endTime: newEndTime,
  };
};

/**
 * Determines the target row or insertion gap index from a vertical drag delta.
 * Row bands occupy [r * (H + GAP), r * (H + GAP) + H].
 * Gap bands between rows are insertion points for new rows.
 * Returns targetSubRow when cursor is over a row, insertionIndex when between rows.
 * Returns both undefined if cursor is outside the track bounds.
 */
const getTargetRowFromDeltaY = (
  deltaY: number,
  originalSubRow: number,
  trackHeight: number,
  trackGap: number,
  totalRows: number
): {targetSubRow?: number; insertionIndex?: number} => {
  const rowStep = trackHeight + trackGap;
  const cursorY = originalSubRow * rowStep + trackHeight / 2 + deltaY;

  // Outside track bounds
  if (cursorY < -trackGap || cursorY > totalRows * rowStep) {
    return {};
  }

  // Above the first row — insertion before row 0
  if (cursorY < 0) {
    return {insertionIndex: 0};
  }

  for (let r = 0; r < totalRows; r++) {
    const rowTop = r * rowStep;
    const rowBottom = rowTop + trackHeight;

    if (cursorY >= rowTop && cursorY <= rowBottom) {
      return {targetSubRow: r};
    }

    // In the gap between row r and row r+1
    if (cursorY > rowBottom && cursorY < rowTop + rowStep) {
      return {insertionIndex: r + 1};
    }
  }

  // Below the last row — insertion after last row
  if (cursorY > (totalRows - 1) * rowStep + trackHeight) {
    return {insertionIndex: totalRows};
  }

  return {};
};

export const formatTime = (seconds: number, showDecimals = true): string => {
  return formatSecondsToTimeString(seconds, {showDecimals});
};

/**
 * Finds the closest snap edge for a given time value within the pixel threshold.
 * Returns the snapped time and the edge position if a snap was found, otherwise returns the original time.
 */
const findSnapEdge = (time: number, edges: number[], pixelsPerSecond: number): {time: number; snappedEdge?: number} => {
  const thresholdTime = SNAP_THRESHOLD_PX / pixelsPerSecond;
  let closest: number | undefined;
  let closestDist = thresholdTime;

  for (const edge of edges) {
    const dist = Math.abs(time - edge);
    if (dist < closestDist) {
      closestDist = dist;
      closest = edge;
    }
  }

  if (closest !== undefined) {
    return {time: closest, snappedEdge: closest};
  }
  return {time};
};

/**
 * Snaps dragged item edges to nearby edges from other items.
 * Returns adjusted times and any active snap guide positions (in pixels).
 */
export const snapDraggedTimes = (startTime: number, endTime: number, edges: number[], pixelsPerSecond: number): {startTime: number; endTime: number; snapLines: number[]} => {
  const duration = endTime - startTime;
  const snapLines: number[] = [];

  const startSnap = findSnapEdge(startTime, edges, pixelsPerSecond);
  const endSnap = findSnapEdge(endTime, edges, pixelsPerSecond);

  // Prefer whichever edge is closer to its target
  const startDist = startSnap.snappedEdge !== undefined ? Math.abs(startTime - startSnap.snappedEdge) : Infinity;
  const endDist = endSnap.snappedEdge !== undefined ? Math.abs(endTime - endSnap.snappedEdge) : Infinity;

  let snappedStart = startTime;
  let snappedEnd = endTime;

  if (startDist <= endDist && startSnap.snappedEdge !== undefined) {
    snappedStart = startSnap.time;
    snappedEnd = snappedStart + duration;
    snapLines.push(timeToPixels(startSnap.snappedEdge, pixelsPerSecond));
  } else if (endSnap.snappedEdge !== undefined && endSnap.time - duration >= 0) {
    // An end-edge snap shifts the whole clip left; skip it when that would push the
    // start below 0 — the commit paths persist the snapped start unclamped.
    snappedEnd = endSnap.time;
    snappedStart = snappedEnd - duration;
    snapLines.push(timeToPixels(endSnap.snappedEdge, pixelsPerSecond));
  }

  return {startTime: snappedStart, endTime: snappedEnd, snapLines};
};

/**
 * Snaps a resized edge to nearby edges from other items.
 * Returns the snapped time and any active snap guide positions (in pixels).
 */
export const snapResizedTime = (time: number, edges: number[], pixelsPerSecond: number): {time: number; snapLines: number[]} => {
  const snapLines: number[] = [];
  const snap = findSnapEdge(time, edges, pixelsPerSecond);

  if (snap.snappedEdge !== undefined) {
    snapLines.push(timeToPixels(snap.snappedEdge, pixelsPerSecond));
  }

  return {time: snap.time, snapLines};
};

/**
 * Collects snap edges from ALL timeline items (canvas items, audio, subtitles)
 * on the current page, excluding the given item ID.
 * Intended to be called imperatively at drag/resize start.
 */
export const getAllSnapEdges = (state: RootState, excludeId: string): number[] => {
  const edges = new Set<number>();
  edges.add(0);

  const currentPageId = state.posterEditor.currentPageId;
  const page = state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId];
  if (!page) {
    return [...edges];
  }

  // Canvas items (non-transcript)
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.TRANSCRIPT || item.uid === excludeId) {
      continue;
    }
    edges.add(item.onPageStartTime);
    edges.add(getItemPageEndTime(item));
  }

  // Audio items
  const audioClips = page.audioClips;
  if (audioClips) {
    for (const audio of Object.values(audioClips.audioItemsHashMap)) {
      if (audio.uid === excludeId) {
        continue;
      }
      edges.add(audio.onPageStartTime);
      edges.add(audio.onPageStartTime + getPlaybackDuration(audio.audioPlayer));
    }
  }

  // Subtitle items
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (item.gitype !== ITEM_TYPE.TRANSCRIPT) {
      continue;
    }
    const transcriptItem = item as {subtitlesHashmap: Record<string, {startTime: number; endTime: number}>};
    for (const [subtitleId, subtitle] of Object.entries(transcriptItem.subtitlesHashmap)) {
      if (subtitleId === excludeId) {
        continue;
      }
      edges.add(subtitle.startTime);
      edges.add(subtitle.endTime);
    }
  }

  return [...edges];
};

export const getNewSubRowForDrag = (data: DragModifyingData): number | undefined => {
  const {targetSubRow, insertionIndex} = data;
  if (targetSubRow !== undefined) {
    return targetSubRow;
  }
  if (insertionIndex !== undefined) {
    return insertionIndex;
  }
  return undefined;
};

export const createResolveVerticalTarget = (draggedItemId: string, timelineItems: TimelineItemData[], totalRows: number): ((params: VerticalDragParams) => VerticalDragResult) => {
  return (params: VerticalDragParams): VerticalDragResult => {
    const {deltaY, startTime: dragStartTime, endTime: dragEndTime} = params;
    const draggedItem = timelineItems.find((item) => item.id === draggedItemId);
    if (!draggedItem) {
      return {};
    }

    const result = getTargetRowFromDeltaY(deltaY, draggedItem.subRow, ITEM_TRACK_HEIGHT, TRACK_GAP, totalRows);

    if (result.targetSubRow !== undefined) {
      const hasOverlap = timelineItems.some(
        (item) => item.id !== draggedItemId && item.subRow === result.targetSubRow && detectOverlap({...draggedItem, startTime: dragStartTime, endTime: dragEndTime}, item)
      );

      if (hasOverlap) {
        const rowStep = ITEM_TRACK_HEIGHT + TRACK_GAP;
        const cursorY = draggedItem.subRow * rowStep + ITEM_TRACK_HEIGHT / 2 + deltaY;
        const rowMidpoint = result.targetSubRow * rowStep + ITEM_TRACK_HEIGHT / 2;

        if (cursorY >= rowMidpoint) {
          return {insertionIndex: result.targetSubRow + 1};
        }
        return {insertionIndex: result.targetSubRow};
      }
    }

    return result;
  };
};

/**
 * Checks whether the current seek position allows splitting the selected timeline item.
 * Both resulting pieces must be at least MIN_ITEM_DURATION long.
 */
export const canSplitTimelineItem = (selectedItem: SelectedTimelineItem, currentPageTime: number): boolean => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return false;
  }

  if (selectedItem.type === TimelineItemType.ITEM || selectedItem.type === TimelineItemType.BACKGROUND) {
    // Splitting plain items is an item-track feature: without the track, fitItemsToVisualDuration
    // resets both halves to the full page span, leaving two identical overlapping copies.
    if (selectedItem.type === TimelineItemType.ITEM && !SHOW_ITEM_TIMELINE_TRACK) {
      return false;
    }
    const item = page.items.itemsHashMap[selectedItem.id];
    if (!item) {
      return false;
    }
    const startTime = item.onPageStartTime;
    const endTime = item.getPageEndTime();
    const leftDuration = currentPageTime - startTime;
    const rightDuration = endTime - currentPageTime;
    return leftDuration >= MIN_ITEM_DURATION && rightDuration >= MIN_ITEM_DURATION;
  }

  if (selectedItem.type === TimelineItemType.AUDIO) {
    const audioItem = page.audioClips.audioItemsHashMap[selectedItem.id];
    if (!audioItem) {
      return false;
    }
    const startTime = audioItem.onPageStartTime;
    const endTime = audioItem.getOnPageEndTime();
    const leftDuration = currentPageTime - startTime;
    const rightDuration = endTime - currentPageTime;
    return leftDuration >= MIN_ITEM_DURATION && rightDuration >= MIN_ITEM_DURATION;
  }

  return false;
};

/**
 * Splits a timeline item at the current seek position into two items.
 * Uses half-open intervals [start, end) so items tile with no gap.
 */
export const splitTimelineItem = async (selectedItem: SelectedTimelineItem, currentPageTime: number): Promise<void> => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }

  if (selectedItem.type === TimelineItemType.ITEM || selectedItem.type === TimelineItemType.BACKGROUND) {
    await page.items.splitItem(selectedItem.id, currentPageTime);
  } else if (selectedItem.type === TimelineItemType.AUDIO) {
    await page.audioClips.splitAudioItem(selectedItem.id, currentPageTime);
  }
};
